import type { Asset } from "schemas/generated/asset";

import * as React from "react";

import {
  PopoverPrimitive,
  PopoverRoot,
  PopoverTrigger,
} from "@replo/design-system/components/popover/Popover";
import twMerge from "@replo/design-system/utils/twMerge";
import { AssetCardActions } from "@v2/features/assets/components/asset-card/AssetCardActions";
import { AssetPreview } from "@v2/features/assets/components/asset-card/AssetPreview";
import { AssetThumbnail } from "@v2/features/assets/components/asset-card/AssetThumbnail";
import { formatAssetDisplayName } from "@v2/features/assets/utils/formatAssetDisplayName";
import { useRequiredContext } from "replo-utils/react/context";

export type AssetCardProps = {
  asset: Asset;
  onClick?: () => void;
  shouldAllowActions?: boolean;
  isUploading?: boolean;
  className?: string;
};

type AssetCardAnchorProps = "onClick" | "className";

const AssetCardContext = React.createContext<{
  isAssetPreviewLoaded: boolean;
  setIsAssetPreviewLoaded: (isAssetPreviewLoaded: boolean) => void;
  asset: Asset;
  handlePopoverOpenChange: (isOpen: boolean) => void;
  anchorRef: React.RefObject<HTMLDivElement>;
  isMenuOpen: boolean;
  setIsMenuOpen: (isMenuOpen: boolean) => void;
  setActionPopoverContent: (
    actionPopoverContent: "delete" | "rename" | null,
  ) => void;
  actionPopoverContent: "delete" | "rename" | null;
  isUploading?: boolean;
} | null>(null);

const useAssetCardContext = () => useRequiredContext(AssetCardContext);

const AssetCardRoot: React.FC<
  React.PropsWithChildren<Omit<AssetCardProps, AssetCardAnchorProps>>
> = (props) => {
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [isAssetPreviewLoaded, setIsAssetPreviewLoaded] = React.useState(false);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [actionPopoverContent, setActionPopoverContent] = React.useState<
    "delete" | "rename" | null
  >(null);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  const handlePopoverOpenChange = (isOpen: boolean) => {
    setIsPopoverOpen(isOpen);
    if (!isOpen) {
      setIsAssetPreviewLoaded(false);
    }
  };

  // NOTE (Max, 2025-09-23): Close popover if anchor is not intersecting (hidden due to overflow)
  // biome-ignore lint/correctness/useExhaustiveDependencies: Close popover when anchor element is not visible due to overflow
  React.useEffect(() => {
    if (!anchorRef.current) {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry && !entry.isIntersecting && isPopoverOpen) {
        handlePopoverOpenChange(false);
      }
    });

    observer.observe(anchorRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isPopoverOpen]);

  const contextValue = {
    isAssetPreviewLoaded,
    setIsAssetPreviewLoaded,
    asset: props.asset,
    handlePopoverOpenChange,
    anchorRef,
    isMenuOpen,
    setIsMenuOpen,
    setActionPopoverContent,
    actionPopoverContent,
    isUploading: props.isUploading,
  };

  return (
    <AssetCardContext.Provider value={contextValue}>
      <PopoverRoot
        open={isPopoverOpen}
        onOpenChange={(open, event) => {
          // NOTE (Ben O, 2026-02-20): Prevent popover from closing on outside press —
          // this is a hover preview that should only close via onMouseLeave.
          if (!open && event.reason === "outside-press") {
            return;
          }
          handlePopoverOpenChange(open);
        }}
      >
        {props.children}
      </PopoverRoot>
    </AssetCardContext.Provider>
  );
};

const AssetCardAnchor: React.FC<
  React.PropsWithChildren<Pick<AssetCardProps, AssetCardAnchorProps>>
> = ({ children, onClick }) => {
  const {
    anchorRef,
    handlePopoverOpenChange,
    asset,
    isMenuOpen,
    actionPopoverContent,
    isUploading,
  } = useAssetCardContext();

  return (
    <PopoverTrigger
      render={
        <div
          ref={anchorRef}
          className={twMerge(
            "border-0.5 border-border rounded-md p-1.5 flex flex-col gap-1",
            onClick && "cursor-pointer",
          )}
        />
      }
      onMouseEnter={() => {
        if (!isMenuOpen && actionPopoverContent === null && !isUploading) {
          handlePopoverOpenChange(true);
        }
      }}
      onMouseLeave={() => {
        handlePopoverOpenChange(false);
      }}
      onClick={onClick}
    >
      <AssetThumbnail asset={asset} isUploading={isUploading} />
      {children}
    </PopoverTrigger>
  );
};

const AssetCardContent: React.FC<
  React.PropsWithChildren<{
    className?: string;
  }>
> = ({ children, className }) => {
  const { asset, isAssetPreviewLoaded, setIsAssetPreviewLoaded } =
    useAssetCardContext();

  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Positioner
        side="right"
        align="start"
        sideOffset={4}
        collisionPadding={30}
        className="isolate z-[100]"
      >
        <PopoverPrimitive.Popup
          initialFocus={false}
          className={twMerge(
            "pointer-events-none isolation-auto flex flex-col gap-1 rounded-md opacity-0 w-[150px] md:w-[350px] lg:w-[450px] bg-white border-0.5 border-border shadow-md p-3 outline-hidden origin-(--transform-origin) data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95",
            // NOTE (Gabe 2025-03-03): We use this trick to prevent layout shift
            // since the popover dimensions are based on the loaded image/video.
            isAssetPreviewLoaded && "opacity-100",
            className,
          )}
        >
          <AssetPreview
            asset={asset}
            onLoad={() => setIsAssetPreviewLoaded(true)}
          />
          {children}
        </PopoverPrimitive.Popup>
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
};

const AssetCardAnchorFooter: React.FC<{
  shouldAllowActions?: boolean;
}> = ({ shouldAllowActions = true }) => {
  const {
    asset,
    isMenuOpen,
    setIsMenuOpen,
    setActionPopoverContent,
    actionPopoverContent,
    isUploading,
  } = useAssetCardContext();

  const displayName = formatAssetDisplayName(asset.name);

  return (
    <div className="relative flex min-h-6 items-center justify-between gap-1">
      <span className="block truncate text-xs font-medium">
        {displayName}
        {asset.fileExtension ? `.${asset.fileExtension}` : ""}
      </span>
      {shouldAllowActions && !isUploading && (
        <AssetCardActions
          asset={asset}
          isMenuOpen={isMenuOpen}
          setIsMenuOpen={setIsMenuOpen}
          setActionPopoverContent={setActionPopoverContent}
          actionPopoverContent={actionPopoverContent}
        />
      )}
    </div>
  );
};

export const AssetCard = (props: AssetCardProps) => {
  return (
    <AssetCardRoot {...props}>
      <AssetCardAnchor
        className={twMerge(props.className, "p-0.5")}
        onClick={props.onClick}
      />
      <AssetCardContent className="p-0.5" />
    </AssetCardRoot>
  );
};

AssetCard.Root = AssetCardRoot;
AssetCard.Anchor = AssetCardAnchor;
AssetCard.Content = AssetCardContent;
AssetCard.AnchorFooter = AssetCardAnchorFooter;
